import { useRef, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import * as api from '../utils/api'
import { compressAvatar } from '../utils/compressAvatar'
import { authslice } from '../store/auth/authSlice'
import { C, fontStack, btn, input, card } from '../constant/theme'

// Self-service profile for any signed-in user: update your own name and password.
export default function Profile() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const user = useSelector((state) => state.auth.user)
  const token = useSelector((state) => state.auth.token)
  const isAdmin = user?.isAdmin || false
  const [mobile, setMobile] = useState(window.innerWidth < 640)

  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 640)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Keep name input in sync with the store (e.g. after a successful save).
  const [name, setName] = useState(user?.name || '')
  useEffect(() => {
    setName(user?.name || '')
  }, [user?.name])

  const [savingName, setSavingName] = useState(false)
  const [nameMsg, setNameMsg] = useState(null)

  const [pw, setPw] = useState({ current: '', next: '', confirm: '' })
  const [savingPw, setSavingPw] = useState(false)
  const [pwMsg, setPwMsg] = useState(null)

  // avatarUrl is derived from the reactive Redux user — no local state needed.
  const [avatarBusy, setAvatarBusy] = useState(false)
  const [avatarMsg, setAvatarMsg] = useState(null)
  const [avatarHover, setAvatarHover] = useState(false)
  const avatarInputRef = useRef(null)

  const syncUser = (updated) => {
    dispatch(authslice.actions.setUser({ token, user: updated }))
  }

  const saveName = async (e) => {
    e.preventDefault()
    setNameMsg(null)
    setSavingName(true)
    try {
      const updated = await api.updateMe({ name: name.trim() })
      syncUser(updated)
      setNameMsg({ ok: true, text: 'Name updated.' })
    } catch (err) {
      setNameMsg({ ok: false, text: err?.response?.data?.message || 'Could not update your name.' })
    } finally {
      setSavingName(false)
    }
  }

  const savePassword = async (e) => {
    e.preventDefault()
    setPwMsg(null)
    if (pw.next !== pw.confirm) {
      setPwMsg({ ok: false, text: 'New passwords do not match.' })
      return
    }
    setSavingPw(true)
    try {
      await api.updateMe({ currentPassword: pw.current, newPassword: pw.next })
      setPw({ current: '', next: '', confirm: '' })
      setPwMsg({ ok: true, text: 'Password changed.' })
    } catch (err) {
      setPwMsg({ ok: false, text: err?.response?.data?.message || 'Could not change your password.' })
    } finally {
      setSavingPw(false)
    }
  }

  const handleAvatarFile = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    setAvatarMsg(null)
    setAvatarBusy(true)
    try {
      const blob = await compressAvatar(file)
      const { signedUrl, gcsKey } = await api.signMyAvatarUpload()
      await fetch(signedUrl, { method: 'PUT', body: blob, headers: { 'Content-Type': 'image/jpeg' } })
      const updated = await api.confirmMyAvatarUpload(gcsKey)
      syncUser(updated)
      setAvatarMsg({ ok: true, text: 'Profile picture updated.' })
    } catch (err) {
      setAvatarMsg({ ok: false, text: err?.response?.data?.message || 'Could not upload picture.' })
    } finally {
      setAvatarBusy(false)
    }
  }

  const handleRemoveAvatar = async () => {
    setAvatarMsg(null)
    setAvatarBusy(true)
    try {
      const updated = await api.removeMyAvatar()
      syncUser(updated)
      setAvatarMsg({ ok: true, text: 'Profile picture removed.' })
    } catch (err) {
      setAvatarMsg({ ok: false, text: err?.response?.data?.message || 'Could not remove picture.' })
    } finally {
      setAvatarBusy(false)
    }
  }

  const initials = (user?.name || '?')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  return (
    <div style={S.root}>
      <header style={{ ...S.header, padding: mobile ? '12px 14px' : '14px 24px' }}>
        <button style={btn('ghost')} onClick={() => navigate(isAdmin ? '/admin' : '/')}>
          ← Back
        </button>
        <strong style={{ color: C.text }}>Your profile</strong>
        <a href="/" style={S.link}>
          Feed ↗
        </a>
      </header>

      <main style={{ ...S.main, padding: mobile ? '16px 14px' : '24px' }}>
        {/* Avatar section */}
        <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', marginBottom: 18 }}>
          <div
            style={S.avatarWrap}
            onClick={() => !avatarBusy && avatarInputRef.current?.click()}
            onMouseEnter={() => setAvatarHover(true)}
            onMouseLeave={() => setAvatarHover(false)}
            title="Click to change picture"
          >
            {user?.avatarUrl ? <img src={user.avatarUrl} alt="avatar" style={S.avatarImg} /> : <div style={S.avatarInitials}>{initials}</div>}
            <div style={{ ...S.avatarOverlay, opacity: avatarBusy ? 1 : avatarHover ? 0.9 : 0 }}>{avatarBusy ? '…' : 'Change'}</div>
          </div>
          <input ref={avatarInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarFile} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 4 }}>{user?.name}</div>
            <div style={{ fontSize: 13, color: C.textSoft, marginBottom: 10 }}>Click the photo to change it</div>
            {user?.avatarUrl && (
              <button style={{ ...btn('danger'), padding: '6px 12px', fontSize: 13 }} onClick={handleRemoveAvatar} disabled={avatarBusy} type="button">
                Remove photo
              </button>
            )}
            {avatarMsg && (
              <div style={{ ...msgStyle(avatarMsg.ok), marginTop: 10, marginBottom: 0 }}>
                {avatarMsg.ok ? '✅' : '⚠️'} {avatarMsg.text}
              </div>
            )}
          </div>
        </div>

        <form style={card} onSubmit={saveName}>
          <h3 style={S.h3}>Your details</h3>
          <Field label="Name">
            <input style={input} value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Email">
            <input style={{ ...input, color: C.textSoft }} value={user?.email || ''} disabled />
          </Field>
          <p style={S.hint}>Need to change your email? Ask an admin to update it for you.</p>
          {nameMsg && (
            <div style={msgStyle(nameMsg.ok)}>
              {nameMsg.ok ? '✅' : '⚠️'} {nameMsg.text}
            </div>
          )}
          <button style={{ ...btn('primary'), marginTop: 4 }} type="submit" disabled={savingName}>
            {savingName ? 'Saving…' : 'Save name'}
          </button>
        </form>

        <form style={{ ...card, marginTop: 18 }} onSubmit={savePassword}>
          <h3 style={S.h3}>Change password</h3>
          <Field label="Current password">
            <input style={input} type="password" value={pw.current} onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))} />
          </Field>
          <Field label="New password">
            <input style={input} type="password" value={pw.next} onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))} placeholder="At least 6 characters" />
          </Field>
          <Field label="Confirm new password">
            <input style={input} type="password" value={pw.confirm} onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))} />
          </Field>
          {pwMsg && (
            <div style={msgStyle(pwMsg.ok)}>
              {pwMsg.ok ? '✅' : '⚠️'} {pwMsg.text}
            </div>
          )}
          <button style={{ ...btn('primary'), marginTop: 4 }} type="submit" disabled={savingPw}>
            {savingPw ? 'Saving…' : 'Change password'}
          </button>
        </form>
      </main>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: '#2e3d52' }}>{label}</label>
      {children}
    </div>
  )
}

const msgStyle = (ok) => ({
  background: ok ? '#cfe8d8' : C.dangerBg,
  color: ok ? C.green : C.danger,
  borderRadius: 8,
  padding: '8px 12px',
  fontSize: 13,
  marginBottom: 12,
})

const S = {
  root: { minHeight: '100vh', background: C.bg, fontFamily: fontStack },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '14px 24px', background: '#fff', borderBottom: `1px solid ${C.cardBorder}` },
  link: { fontSize: 13, color: C.navy2, textDecoration: 'none', fontWeight: 600 },
  main: { maxWidth: 520, margin: '0 auto', padding: '24px' },
  h3: { margin: '0 0 14px', fontSize: 15, color: C.text },
  hint: { fontSize: 12.5, color: C.textSoft, margin: '0 0 12px' },
  avatarWrap: {
    position: 'relative',
    width: 80,
    height: 80,
    borderRadius: '50%',
    overflow: 'hidden',
    cursor: 'pointer',
    flexShrink: 0,
    border: `2px solid ${C.cardBorder}`,
  },
  avatarImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  avatarInitials: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, ${C.navy2} 0%, #0d2640 100%)`,
    color: '#fff',
    fontSize: 26,
    fontWeight: 800,
    letterSpacing: 1,
  },
  avatarOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 13,
    fontWeight: 700,
    transition: 'opacity .18s',
  },
}
